/**
 * Canonical Questionnaire — answer resolution.
 *
 * This part of the renderer RECEIVES answers from its parent; it never fetches
 * or saves them. Its only job is deciding which supplied value a widget shows.
 *
 * TWO DISTINCT QUESTIONS, deliberately kept apart:
 *
 *   1. "Does a supplied answer ENTRY exist?"  -> {@link readSuppliedEntry}
 *      A key present in the answer map means the parent has an opinion about
 *      this question, whatever the value is. This is what makes an explicit
 *      clear stick: the user emptied the field, we persisted that, and the
 *      SHAPE pre-fill must NOT come back to refill it.
 *
 *   2. "Is the VALUE meaningfully answered?"  -> {@link isMeaningfullyAnswered}
 *      Whether there is real content to show. Drives which of the candidate
 *      values wins, and is what a caller asks when it wants "is this filled in".
 *
 * Conflating the two is what lets a pre-fill resurrect a cleared answer, so the
 * two never share a code path.
 *
 * Precedence (highest first):
 *   1. a persisted entry, if one exists at all — meaningful value, else the
 *      widget's empty state (never the pre-fill)
 *   2. pre-filled (SHAPE) answer, if meaningful
 *   3. the widget's own empty/default state
 *
 * Truthiness is deliberately NOT used anywhere here:
 *   - `false` and `0` are meaningful answers and win over a pre-fill.
 *   - `null` / `undefined` are meaningfully unanswered.
 *   - `""` and whitespace-only strings are meaningfully unanswered.
 *   - `[]` and `{}` are meaningfully unanswered BY DEFAULT — but an entry
 *     holding one is still an entry, so it blocks the pre-fill.
 *   - arrays / objects are passed through by reference-preserving structure;
 *     nothing is flattened, coerced, or reshaped.
 *
 * WIDGET-SPECIFIC RULES. A structured answer can be "shaped but empty" —
 * `{ teamMembers: [] }`, `[{ name: "", website: "" }]`, `{ defaults: [], others: [] }`
 * are all non-empty containers holding nothing a user typed. Those widgets
 * supply their own `isMeaningfulContent` predicate (see `widgetRegistry.js`,
 * which points them at {@link hasMeaningfulLeaf}) rather than bending the
 * default rule for everyone.
 */

/** Keys that identify a persisted-answer / pre-fill envelope rather than a raw value. */
const ENVELOPE_MARKERS = ["canonical_question_id", "source", "entity_id", "element_key", "evidence"];

function isPlainObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Is `value` meaningful content — something the user actually answered?
 *
 * `false` and `0` are answers. `null`, `undefined`, blank strings, empty arrays
 * and empty objects are not. Pass `isMeaningfulContent` to override the rule for
 * a structured value whose emptiness the generic check cannot see.
 *
 * @param {*} value
 * @param {{ isMeaningfulContent?: (value: *) => boolean }} [options]
 */
export function isMeaningfullyAnswered(value, options = {}) {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;

  if (Array.isArray(value) || isPlainObject(value)) {
    const { isMeaningfulContent } = options;
    if (typeof isMeaningfulContent === "function") return isMeaningfulContent(value) === true;
    return Array.isArray(value) ? value.length > 0 : Object.keys(value).length > 0;
  }

  // Numbers (including 0), booleans (including false), and anything exotic.
  return true;
}

/**
 * Does a structured value contain at least one meaningful leaf?
 *
 * The shared opt-in rule for widgets whose empty state is a populated shape:
 * `{ teamMembers: [] }`, `[{ name: "", website: "" }]` and
 * `{ address: null, city: null }` all report false, while a single filled field
 * anywhere inside reports true. `false` and `0` count as filled.
 */
export function hasMeaningfulLeaf(value) {
  if (Array.isArray(value)) return value.some((item) => hasMeaningfulLeaf(item));
  if (isPlainObject(value)) return Object.values(value).some((item) => hasMeaningfulLeaf(item));
  return isMeaningfullyAnswered(value);
}

/**
 * Is `value` an empty container — the shape a widget emits when the user clears
 * it? Used only to infer intent for callers of {@link resolveAnswer} that pass
 * bare values with no entry context; {@link resolveQuestionAnswer} knows whether
 * the entry exists and does not need to guess.
 */
export function isExplicitClear(value) {
  if (Array.isArray(value)) return value.length === 0;
  if (isPlainObject(value)) return Object.keys(value).length === 0;
  return false;
}

/**
 * Is `value` an answer with real content?
 *
 * Alias of {@link isMeaningfullyAnswered}, kept as the short name most call
 * sites read better with. NOTE this asks about the VALUE, not about whether an
 * entry exists — for that, use {@link readSuppliedEntry}.
 */
export function isAnswered(value, options = {}) {
  return isMeaningfullyAnswered(value, options);
}

/** Convenience inverse of {@link isAnswered}, for readability at call sites. */
export function isUnanswered(value, options = {}) {
  return !isAnswered(value, options);
}

/**
 * Unwrap `{ value, source, canonical_question_id, ... }` envelopes as returned
 * by the canonical-answer and SHAPE pre-fill APIs. A bare answer that happens
 * to be an object (address `formData`, industry `formData`, logo result, ...)
 * is returned untouched, because it carries none of the envelope markers.
 */
export function unwrapAnswerValue(entry) {
  if (!isPlainObject(entry)) return entry;
  if (!Object.hasOwn(entry, "value")) return entry;
  const looksLikeEnvelope = ENVELOPE_MARKERS.some((marker) => Object.hasOwn(entry, marker));
  return looksLikeEnvelope ? entry.value : entry;
}

/**
 * Read one answer out of a supplied map, reporting whether an entry EXISTED as
 * well as its unwrapped value.
 *
 * Lookup is by canonical-question id first, then by canonical machine key, so
 * a parent may key its map either way. A present-but-null entry is honoured as
 * "explicitly no answer" rather than falling through to the other key — hence
 * `exists` rather than "is the value non-null".
 *
 * @returns {{ exists: boolean, value: * }}
 */
export function readSuppliedEntry(answers, canonicalQuestionId, machineKey) {
  if (!isPlainObject(answers)) return { exists: false, value: undefined };

  for (const key of [canonicalQuestionId, machineKey]) {
    if (key && Object.hasOwn(answers, key)) {
      return { exists: true, value: unwrapAnswerValue(answers[key]) };
    }
  }

  return { exists: false, value: undefined };
}

/**
 * The value half of {@link readSuppliedEntry}, for callers that only need it.
 * `undefined` is ambiguous here (missing key vs an entry holding `undefined`),
 * which is exactly why the resolver uses `readSuppliedEntry` instead.
 */
export function readSuppliedAnswer(answers, canonicalQuestionId, machineKey) {
  return readSuppliedEntry(answers, canonicalQuestionId, machineKey).value;
}

/**
 * Apply the precedence rules. `emptyValue` is the widget's own empty state and
 * is used verbatim when nothing meaningful is available.
 *
 * @param {*}        persisted           the persisted value
 * @param {*}        prefilled           the pre-fill candidate
 * @param {*}        emptyValue          the widget's empty/default state
 * @param {boolean} [persistedProvided]  whether a persisted ENTRY exists. When
 *   omitted it is inferred from the value alone (meaningful content, or an empty
 *   container left behind by a clear) — pass it explicitly whenever the caller
 *   knows, because inference cannot tell a cleared `null` from an absent one.
 * @param {function} [isMeaningfulContent]  widget-specific meaningful-content rule
 */
export function resolveAnswer({ persisted, prefilled, emptyValue = null, persistedProvided, isMeaningfulContent }) {
  const options = { isMeaningfulContent };

  const hasPersistedEntry =
    persistedProvided === undefined
      ? isMeaningfullyAnswered(persisted, options) || isExplicitClear(persisted)
      : persistedProvided === true;

  // A persisted entry is the parent's final word. If it holds no meaningful
  // content the user cleared the field, so we fall to the widget's empty state
  // and NEVER back to the pre-fill.
  if (hasPersistedEntry) {
    return isMeaningfullyAnswered(persisted, options) ? persisted : emptyValue;
  }

  if (isMeaningfullyAnswered(prefilled, options)) return prefilled;
  return emptyValue;
}

/**
 * Resolve the value a single question should display.
 *
 * @param {object}   question             normalized question from `composition.js`
 * @param {object}   persistedAnswers     map keyed by canonical id or machine key
 * @param {object}   prefilledAnswers     map keyed by canonical id or machine key
 * @param {*}        emptyValue           the widget's empty/default state
 * @param {function} [isMeaningfulContent] widget-specific meaningful-content rule
 */
export function resolveQuestionAnswer({
  question,
  persistedAnswers,
  prefilledAnswers,
  emptyValue = null,
  isMeaningfulContent,
}) {
  const canonicalQuestionId = question?.canonicalQuestionId || null;
  const machineKey = question?.machineKey || null;
  const options = { isMeaningfulContent };

  // Entry existence, not value truthiness: a persisted clear must survive.
  const persistedEntry = readSuppliedEntry(persistedAnswers, canonicalQuestionId, machineKey);

  // A pre-fill supplied by the parent wins over the one embedded in the
  // composition response, so a caller can override SHAPE data if it needs to.
  const suppliedPrefill = readSuppliedEntry(prefilledAnswers, canonicalQuestionId, machineKey).value;
  const prefilled = isMeaningfullyAnswered(suppliedPrefill, options) ? suppliedPrefill : question?.prefillValue;

  return resolveAnswer({
    persisted: persistedEntry.value,
    persistedProvided: persistedEntry.exists,
    prefilled,
    emptyValue,
    isMeaningfulContent,
  });
}
